import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, DollarSign, Briefcase, BarChart3, Activity, Wallet, CreditCard, Repeat, Zap, Wifi, Shield, Home, Film } from 'lucide-react';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import UpcomingPayments from '../UpcomingPayments';

const DashboardTab = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load financial + summary data in parallel
      const [financialData, summary] = await Promise.allSettled([
        apiService.getDashboardStats(),
        apiService.getDashboardSummary()
      ]);

      if (financialData.status === 'fulfilled') {
        setStats(financialData.value);
      }

      if (summary.status === 'fulfilled') {
        setSummaryData(summary.value);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2
    }).format(value);
  };

  const handleCategoryClick = (category) => {
    // Navigate to transactions tab with category filter
    navigate(`/?tab=transactions&category=${encodeURIComponent(category)}`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold bg-gradient-to-br from-sky-500 to-violet-500 bg-clip-text text-transparent">FiGuru Dashboard</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Your personal finance — accounts, transactions, and receipts in one place</p>
      </div>

      {/* Upcoming Payments — what's due when, across all accounts */}
      <UpcomingPayments />

      {/* Financial Stats Cards */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <DollarSign className="h-5 w-5 mr-2" />
          Financial Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Value */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Net Worth</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatCurrency(stats?.total_value || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Accounts Count */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Briefcase className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Active Accounts</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats?.total_accounts || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* 30-Day Change */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">30-Day Change</dt>
                  <dd className={`text-lg font-medium flex items-center ${
                    (stats?.total_return || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(stats?.total_return || 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4 mr-1" />
                    ) : (
                      <TrendingDown className="h-4 w-4 mr-1" />
                    )}
                    {formatPercent(stats?.total_return || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* 30-Day Spending */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">30-Day Spending</dt>
                  <dd className="text-lg font-medium text-red-600">
                    {formatCurrency(stats?.spending_last_30_days || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Cash Flow Summary */}
      {summaryData && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Wallet className="h-5 w-5 mr-2 text-green-500" />
            Cash Flow Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Available Cash */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 overflow-hidden shadow rounded-lg border border-green-200 dark:border-green-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-green-100 dark:bg-green-800 rounded-lg">
                    <Wallet className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-green-700 dark:text-green-300 truncate">Available Cash</dt>
                      <dd className="text-xl font-bold text-green-800 dark:text-green-200">
                        {formatCurrency(summaryData.available_cash || 0)}
                      </dd>
                      <dd className="text-xs text-green-600 dark:text-green-400">Checking & Savings</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Credit Due */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 overflow-hidden shadow rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-orange-100 dark:bg-orange-800 rounded-lg">
                    <CreditCard className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-orange-700 dark:text-orange-300 truncate">Credit Due</dt>
                      <dd className="text-xl font-bold text-orange-800 dark:text-orange-200">
                        {formatCurrency(summaryData.credit_due || 0)}
                      </dd>
                      <dd className="text-xs text-orange-600 dark:text-orange-400">Unpaid Statement Balances</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Available */}
            {(() => {
              const netAvailable = (summaryData.available_cash || 0) - (summaryData.credit_due || 0);
              const isPositive = netAvailable >= 0;
              return (
                <div className={`bg-gradient-to-br ${isPositive ? 'from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-200 dark:border-blue-800' : 'from-red-50 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30 border-red-200 dark:border-red-800'} overflow-hidden shadow rounded-lg border`}>
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 p-3 ${isPositive ? 'bg-blue-100 dark:bg-blue-800' : 'bg-red-100 dark:bg-red-800'} rounded-lg`}>
                        {isPositive ? (
                          <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <TrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className={`text-sm font-medium ${isPositive ? 'text-blue-700 dark:text-blue-300' : 'text-red-700 dark:text-red-300'} truncate`}>Net Available</dt>
                          <dd className={`text-xl font-bold ${isPositive ? 'text-blue-800 dark:text-blue-200' : 'text-red-800 dark:text-red-200'}`}>
                            {formatCurrency(Math.abs(netAvailable))}
                          </dd>
                          <dd className={`text-xs ${isPositive ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>Cash minus Credit Due</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Recurring Payments Section */}
      {summaryData && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Repeat className="h-5 w-5 mr-2 text-purple-500" />
              Recurring Payments
            </h2>
            {summaryData.recurring_count > 0 && (
              <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                {formatCurrency(summaryData.recurring_total || 0)}/mo
              </span>
            )}
          </div>

          {summaryData.recurring_payments && summaryData.recurring_payments.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {summaryData.recurring_payments.map((payment) => {
                  const getCategoryIcon = (category) => {
                    switch (category?.toLowerCase()) {
                      case 'utilities': return Zap;
                      case 'internet': case 'phone': return Wifi;
                      case 'insurance': return Shield;
                      case 'rent': case 'housing': case 'mortgage': return Home;
                      case 'entertainment': case 'subscription': return Film;
                      default: return Repeat;
                    }
                  };
                  const IconComponent = payment.category_icon ? null : getCategoryIcon(payment.category_name);

                  return (
                    <div key={payment.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className="flex items-center">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg mr-3">
                          {payment.category_icon ? (
                            <span className="text-lg">{payment.category_icon}</span>
                          ) : (
                            <IconComponent className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{payment.payee}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {payment.recurrence_label || 'Monthly'}
                            {payment.account_name && ` • ${payment.account_name}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                          {formatCurrency(payment.amount || 0)}
                        </span>
                        {payment.monthly_amount !== payment.amount && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            ({formatCurrency(payment.monthly_amount)}/mo)
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
              <Repeat className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No recurring payments</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add recurring payments to track your bills and subscriptions</p>
            </div>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 Spending Categories */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Top Spending Categories (30 Days)</h3>
          {stats?.top_categories && stats.top_categories.length > 0 ? (
            <div className="space-y-3">
              {stats.top_categories.map((cat, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(cat.category)}
                  className="flex items-center justify-between w-full hover:bg-gray-50 dark:hover:bg-gray-700 -mx-2 px-2 py-2 rounded-md transition-colors cursor-pointer"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-3 w-6">{index + 1}.</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize truncate">
                      {cat.category}
                    </p>
                  </div>
                  <div className="flex items-center text-red-600 ml-3">
                    <span className="text-sm font-medium">
                      {formatCurrency(Math.abs(cat.amount))}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No spending data available</p>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Transactions</h3>
          {stats?.recent_transactions && stats.recent_transactions.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_transactions.map((tx, index) => (
                <div key={tx.id || index} className="flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white truncate font-medium">{tx.description}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{tx.account} • {formatDate(tx.date)}</p>
                  </div>
                  <div className={`ml-3 font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(tx.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No recent transactions</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;